import * as React from "react";
import { api } from "@/lib/api";
import { Check, ChevronsUpDown, Mail, Users, X as XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * 🆕 Feb 2026 — StaffEmailCombobox
 * Combobox per selezionare rapidamente un membro staff dell'agenzia
 * (auto-carica `/agency/staff-presence`), con possibilità di digitare
 * una email diversa a mano. Il valore selezionato è sempre una email (string).
 *
 * Props:
 *   - value: string (email corrente)
 *   - onChange: (email: string) => void
 *   - placeholder: string
 *   - testid: string
 *   - defaultToCurrentUser: bool — se true e value è vuoto, prova a pre-selezionare
 *     l'email dell'utente loggato dopo il caricamento della lista.
 */
export default function StaffEmailCombobox({
  value,
  onChange,
  placeholder = "Seleziona uno staff o digita un'email…",
  testid = "staff-email-combobox",
  defaultToCurrentUser = false,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  const [staff, setStaff] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/agency/staff-presence");
        if (!mounted) return;
        const arr = Array.isArray(data) ? data.filter((s) => s.email) : [];
        setStaff(arr);
        if (defaultToCurrentUser && !value) {
          try {
            const me = JSON.parse(localStorage.getItem("user") || "null");
            const found = me?.email && arr.find((s) => s.email?.toLowerCase() === me.email.toLowerCase());
            if (found) onChange?.(found.email);
          } catch { /* noop */ }
        }
      } catch {
        // Silente: se non riusciamo a caricare lo staff, il combobox resta usabile per input libero
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const selectedStaff = React.useMemo(
    () => staff.find((s) => (s.email || "").toLowerCase() === (value || "").toLowerCase()),
    [staff, value],
  );

  const trimmedSearch = (search || "").trim();
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(trimmedSearch);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-slate-400",
          )}
        >
          <span className="inline-flex items-center gap-2 truncate">
            <Mail className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="truncate">
              {value
                ? (selectedStaff
                  ? `${selectedStaff.name || selectedStaff.email} · ${selectedStaff.email}`
                  : value)
                : placeholder}
            </span>
          </span>
          <span className="flex items-center gap-1">
            {value && (
              <span
                role="button"
                tabIndex={-1}
                data-testid={`${testid}-clear`}
                onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                className="h-5 w-5 inline-flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"
                title="Rimuovi selezione"
              >
                <XIcon className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(28rem,92vw)] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command
          filter={(itemValue, s) => {
            return itemValue.toLowerCase().includes(s.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder="Cerca staff per nome o email, oppure digita un'email…"
            value={search}
            onValueChange={setSearch}
            data-testid={`${testid}-input`}
          />
          <CommandList className="max-h-[50vh] overflow-y-auto">
            <CommandEmpty>
              <div className="text-sm py-3 text-center text-slate-500">
                {loading ? "Caricamento staff…" : "Nessuno staff trovato."}
                {looksLikeEmail && (
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { onChange?.(trimmedSearch); setOpen(false); setSearch(""); }}
                      data-testid={`${testid}-use-custom`}
                      className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                    >
                      Usa email digitata: <strong className="ml-1">{trimmedSearch}</strong>
                    </Button>
                  </div>
                )}
              </div>
            </CommandEmpty>

            {/* CTA: usa email digitata a mano (anche se la lista staff ha risultati) */}
            {looksLikeEmail && !staff.some((s) => s.email?.toLowerCase() === trimmedSearch.toLowerCase()) && (
              <CommandGroup heading="Email personalizzata">
                <CommandItem
                  value={`__custom__${trimmedSearch}`}
                  onSelect={() => { onChange?.(trimmedSearch); setOpen(false); setSearch(""); }}
                  data-testid={`${testid}-custom-item`}
                >
                  <Mail className="h-4 w-4 mr-2 text-indigo-600" />
                  <span className="font-medium">Usa email:</span>&nbsp;<span className="font-mono">{trimmedSearch}</span>
                </CommandItem>
              </CommandGroup>
            )}

            {staff.length > 0 && (
              <CommandGroup heading={<span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> Staff agenzia</span>}>
                {staff.map((s) => {
                  const hay = `${s.name || ""} ${s.email || ""} ${s.role || ""}`.toLowerCase();
                  return (
                    <CommandItem
                      key={s.id}
                      value={hay}
                      onSelect={() => { onChange?.(s.email); setOpen(false); setSearch(""); }}
                      data-testid={`${testid}-staff-${s.id}`}
                    >
                      <Check
                        className={cn(
                          "mr-1 h-4 w-4",
                          (value || "").toLowerCase() === (s.email || "").toLowerCase() ? "opacity-100 text-emerald-600" : "opacity-0",
                        )}
                      />
                      <div className="flex flex-col min-w-0">
                        <div className="font-medium truncate">{s.name || s.email}</div>
                        <div className="text-xs text-slate-500 truncate">
                          <span className="font-mono">{s.email}</span>
                          {s.role && <span className="ml-2 uppercase text-[10px] bg-slate-100 rounded px-1 py-0.5">{s.role}</span>}
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { StaffEmailCombobox };
